import { ActionTypes, AppActions } from './types';
import get from 'lodash/get';

import {
  RECEIVE_CONFIGURATION,
  RECEIVE_SWITCHOPTIONSRESULT,
  SET_LOAD_CODE,
} from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';

const initialState = {
  currentStep: 'start',
  selectedSystem: null,
  selectedProductType: null,
  systems: [],
  productTypes: [],
  systemParameters: [],
  productParamsValidation: {},
  configuratorMenuOptions: [],
  selectedConfiguratorMenu: null,
  configurationCodeToBeLoaded: null,
  languages: [],
  countries: [],
  authMessage: null,
  disabledOptions: [],
  productParameterFormData: {},
  productParameterWarnings: null,
  productParameterWarningsApproved: false,
  accessDeniedError: null,
  userFlags: null,
};

const AppReducer = (state = initialState, action: AppActions) => {
  switch (action.type) {
    case ActionTypes.SET_CURRENT_STEP: {
      return {
        ...state,
        currentStep: action.payload,
      };
    }
    case ActionTypes.SET_SELECTED_SYSTEM: {
      return {
        ...state,
        selectedSystem: action.payload,
      };
    }
    case ActionTypes.SET_SELECTED_PRODUCT_TYPE: {
      return {
        ...state,
        selectedProductType: action.payload,
      };
    }
    case ActionTypes.SET_MENU: {
      const { systems, productTypes } = action.payload;
      let { selectedSystem, selectedProductType } = state;
      if (selectedSystem?.identifier) {
        selectedSystem = systems.find((s) => s.identifier === selectedSystem.identifier) || selectedSystem;
      }
      if (selectedProductType?.identifier) {
        selectedProductType =
          productTypes.find((p) => p.identifier === selectedProductType.identifier) || selectedProductType;
      }
      return {
        ...state,
        systems,
        productTypes,
        selectedSystem,
        selectedProductType,
      };
    }
    case ActionTypes.SET_SYSTEM_DATA_OPTIONS: {
      return {
        ...state,
        systemParameters: action.payload,
      };
    }
    case ActionTypes.SET_CONFIGURATOR_MENU_OPTIONS: {
      return {
        ...state,
        configuratorMenuOptions: action.payload,
      };
    }
    case ActionTypes.SET_SELECTED_CONFIGURATOR_MENU: {
      return {
        ...state,
        selectedConfiguratorMenu: action.payload,
      };
    }
    case ActionTypes.SET_SELECTED_COMPONENT: {
      return {
        ...state,
        selectedComponent: action.payload ? { ...action.payload } : null,
      };
    }
    case ActionTypes.SET_PRODUCT_PARAM_VALID: {
      if (!action.payload) {
        return { ...state, productParamsValidation: {} };
      }
      return {
        ...state,
        productParamsValidation: {
          ...state.productParamsValidation,
          ...action.payload,
        },
      };
    }
    case SET_LOAD_CODE: {
      return {
        ...state,
        configurationCodeToBeLoaded: action.loadCode,
      };
    }
    case RECEIVE_CONFIGURATION: {
      state = { ...updateMenuComponentActiveState(state, action.data) };
      if (state.configurationCodeToBeLoaded && state.configurationCodeToBeLoaded === action.data?.code) {
        // restore persisted appState
        const appState = action.data?.customdata?.appState || {};
        return {
          ...state,
          ...appState,
          configurationCodeToBeLoaded: null,
          selectedConfiguratorMenu: null,
        };
      }
      return state;
    }
    case ActionTypes.SET_LANGUAGES: {
      return {
        ...state,
        languages: action.payload,
      };
    }
    case ActionTypes.SET_COUNTRIES: {
      return {
        ...state,
        countries: action.payload,
      };
    }
    case ActionTypes.SET_PRODUCT_PARAMETER_FORM_DATA: {
      return {
        ...state,
        productParameterFormData: {
          ...state.productParameterFormData,
          [action.productIdentifier]: action.formData,
        },
      };
    }
    case ActionTypes.SET_PRODUCT_PARAMETER_WARNINGS: {
      return {
        ...state,
        productParameterWarnings: action.warnings,
        productParameterWarningsApproved: false,
      };
    }
    case ActionTypes.SET_PRODUCT_PARAMETER_WARNINGS_APPROVED: {
      return {
        ...state,
        productParameterWarningsApproved: true,
      };
    }
    case ActionTypes.SET_ACCESS_DENIED_ERROR: {
      if (state.accessDeniedError !== null) return state;
      return { ...state, accessDeniedError: action.payload };
    }
    case ActionTypes.CLEAR_ACCESS_DENIED_ERROR: {
      return { ...state, accessDeniedError: null };
    }
    case ActionTypes.SET_USER_FLAGS: {
      return { ...state, userFlags: action.payload };
    }
    case RECEIVE_SWITCHOPTIONSRESULT: {
      const nextState = updateMenuComponentActiveState(state, action.data);
      return {
        ...nextState,
      };
    }
    default:
      return state;
  }
};

const updateMenuComponentActiveState = (appState, switchOptionData) => {
  const { configuratorMenuOptions, systemParameters } = appState;
  const { optionclassifications } = switchOptionData;
  if (!configuratorMenuOptions || !optionclassifications) {
    return appState;
  }
  const updatedMenu = prepareMenuGroups(configuratorMenuOptions, optionclassifications);
  return {
    ...appState,
    configuratorMenuOptions: updatedMenu,
    systemParameters: updateActiveState(systemParameters, optionclassifications),
  };
};

const updateActiveState = (menuComponent, optionclassifications) => {
  return menuComponent.map((menuOption) => {
    const optionClassification = optionclassifications.find(
      (optionclassification) => optionclassification.identifier === menuOption.identifier
    );
    if (optionClassification) {
      menuOption.active = get(optionClassification, 'active', true);
      menuOption.selectedOptions = get(optionClassification, 'selectedoptions', null);
    }
    if (menuOption.entries) {
      menuOption.entries = updateActiveState(menuOption.entries, optionclassifications);
    }
    return menuOption;
  });
};

const updateMenuWithSelectedOptions = (appState) => {
  const { configuratorMenuOptions, componentList, selectedConfiguratorMenu, selectedComponent } = appState;
  if (!componentList?.length) {
    return appState;
  }
  const updatedMenu = prepareMenuGroups(configuratorMenuOptions, componentList);

  const selectedBlockingOptions = [];

  for (const component of componentList) {
    if (component.selectedoptions?.length) {
      component.selectedoptions[0].attributes.forEach((attribute) => {
        if (attribute.identifier === 'blocks_option' && attribute.values?.length) {
          selectedBlockingOptions.push(...attribute.values.map(({ value }) => value));
        }
      });
    }
  }
  return {
    ...appState,
    configuratorMenuOptions: updatedMenu,
    selectedComponent: updatedMenu
      .find(({ identifier }) => identifier === selectedConfiguratorMenu)
      ?.elements?.find(({ identifier }) => identifier === selectedComponent?.identifier),
    disabledOptions: selectedBlockingOptions,
  };
};

const prepareSelectedOptions = (items, components) => {
  return items.map((itemOrig) => {
    const item = { ...itemOrig };
    // check if it is a group with further entries
    if (item.entries) {
      return {
        ...item,
        entries: prepareSelectedOptions(item.entries, components),
      };
    }
    // otherwise it should be a component
    const component = components.find(({ identifier }) => identifier === item.identifier);
    const selectedOptions = component?.selectedoptions;
    const firstOption = (selectedOptions?.length && selectedOptions[0]) || null;
    return {
      ...item,
      selectedOption: firstOption,
      active: component ? component.active : item.active,
    };
  });
};

const prepareMenuGroups = (menuGroups, components) =>
  menuGroups.map((menuGroup) => ({
    ...menuGroup,
    elements: prepareSelectedOptions(menuGroup.elements, components),
  }));

const AppReducerWrapper = (state = initialState, action: AppActions) => {
  const nextState = AppReducer(state, action);
  const componentList = action?.data?.optionclassifications;
  if (componentList && nextState.componentList !== componentList) {
    return updateMenuWithSelectedOptions({
      ...nextState,
      componentList,
    });
  }
  if (
    state.configuratorMenuOptions !== nextState.configuratorMenuOptions ||
    state.selectedConfiguratorMenu !== nextState.selectedConfiguratorMenu
  ) {
    return updateMenuWithSelectedOptions(nextState);
  }
  return nextState;
};

export default AppReducerWrapper;
